import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  FolderCheckIcon,
  FolderXIcon,
  FoldersIcon,
  InfoIcon,
  LayoutListIcon,
  LogOut,
  Settings,
  User,
} from 'lucide-react'

import { Sidebar, SidebarContent, SidebarProvider } from '@/components/sidebar/sidebar'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { useLocalStorage } from '@/lib/hooks/use-local-storage'
import { logoutMutation } from '@/lib/queries/auth/business/logout/mutations'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { cn } from '@/lib/utils'

import { AddApplicantButton } from './add-applicant-button/add-applicant-button'
import { MenuGroup } from './menu-group/menu-group'
import { MenuItem } from './menu-item/menu-item'
import { ViewPlan } from './view-plan/view-plan'

const CURRENT_PRODUCT_UPDATE_NOTE_URL =
  'https://north-galley-04c.notion.site/Lawfully-Pro-Dashboard-for-Data-Visualization-Updated-6-3-2025-207818cec2c980d7b55fe03874318575'

export const SideNavigationBar: React.FC = () => {
  const { data: dataUser } = useQuery(getUserQuery)
  const { mutateAsync: logout } = useMutation(logoutMutation)

  const { value: recentProductUpdateNoteUrl, setItem: setRecentProductUpdateNoteUrl } = useLocalStorage<string>({
    key: LOCAL_STORAGE_KEY_MAP.recentProductUpdateNoteUrl,
  })

  const showsNewBadgeOnProductUpdate =
    !recentProductUpdateNoteUrl || recentProductUpdateNoteUrl !== CURRENT_PRODUCT_UPDATE_NOTE_URL

  const handleLogoutButton = async () => {
    await logout(null, {
      onSuccess: () => {
        localStorage.removeItem(LOCAL_STORAGE_KEY_MAP.token)
        window.location.href = '/'
      },
      onError: (error) => {
        console.error('Logout failed:', error)
      },
    })
  }

  const handleClickProductUpdateMenu = () => {
    setRecentProductUpdateNoteUrl(CURRENT_PRODUCT_UPDATE_NOTE_URL)
    window.open(CURRENT_PRODUCT_UPDATE_NOTE_URL, '_blank')
  }

  return (
    <SidebarProvider>
      <Sidebar className={cn(dataUser?.hasSubscription ? 'pt-nav-bar' : `pt-nav-bar-top-banner`)}>
        <SidebarContent className={cn('pb-8 pt-7')}>
          <div className={cn('flex flex-col gap-2', 'mb-8 px-3')}>
            <AddApplicantButton />
          </div>

          <div className={cn('flex w-full flex-1 flex-col')}>
            <div className={cn('border-b pb-6')}>
              <div className={cn('mb-4 px-3')}>
                <Link to={'/dashboard'}>
                  {({ isActive }) => (
                    <MenuItem icon={<LayoutListIcon size={16} />} text="Dashboard" isActive={isActive} />
                  )}
                </Link>
              </div>

              <div className={cn('mb-2 px-7 text-lg font-semibold')}>List</div>
              <div className={cn('flex flex-col space-y-1 px-3')}>
                <Link
                  to={'/applicants'}
                  activeOptions={{ includeSearch: false }}
                  search={{
                    page: 1,
                    sortKey: 'id',
                    sortOrder: 'desc',
                  }}
                >
                  {({ isActive }) => <MenuItem icon={<User size={16} />} text="Applicants" isActive={isActive} />}
                </Link>

                <MenuGroup
                  icon={<FoldersIcon size={16} />}
                  label="Immigration Cases"
                  matchingPaths={['/cases/active', '/cases/inactive']}
                >
                  <Link
                    to={'/cases/active'}
                    activeOptions={{ includeSearch: false }}
                    search={{
                      page: 1,
                    }}
                  >
                    {({ isActive }) => (
                      <MenuItem icon={<FolderCheckIcon size={16} />} text="Active Cases" isActive={isActive} />
                    )}
                  </Link>
                  <Link
                    to={'/cases/inactive'}
                    activeOptions={{ includeSearch: false }}
                    search={{
                      page: 1,
                    }}
                  >
                    {({ isActive }) => (
                      <MenuItem icon={<FolderXIcon size={16} />} text="Inactive Cases" isActive={isActive} />
                    )}
                  </Link>
                </MenuGroup>

                <MenuGroup
                  icon={<FoldersIcon size={16} />}
                  label="EOIR Cases"
                  matchingPaths={['/cases-eoir/active', '/cases-eoir/inactive']}
                >
                  <Link
                    to={'/cases-eoir/active'}
                    activeOptions={{ includeSearch: false }}
                    search={{
                      page: 1,
                    }}
                  >
                    {({ isActive }) => (
                      <MenuItem icon={<FolderCheckIcon size={16} />} text="Active Cases" isActive={isActive} />
                    )}
                  </Link>
                  <Link
                    to={'/cases-eoir/inactive'}
                    activeOptions={{ includeSearch: false }}
                    search={{
                      page: 1,
                    }}
                  >
                    {({ isActive }) => (
                      <MenuItem icon={<FolderXIcon size={16} />} text="Inactive Cases" isActive={isActive} />
                    )}
                  </Link>
                </MenuGroup>
              </div>
            </div>
          </div>

          <div className={cn('flex-0 flex w-full flex-col gap-1 px-3', !dataUser?.hasSubscription && 'mb-6')}>
            <MenuItem
              icon={<InfoIcon size={16} />}
              text="Product Update"
              asChild={false}
              rightComponent={
                showsNewBadgeOnProductUpdate && (
                  <span
                    className={cn(
                      'inline-block',
                      'ml-auto',
                      'bg-blue-100',
                      'rounded-[10px]',
                      'px-2 py-0.5',
                      'text-xs font-medium text-gray-900'
                    )}
                  >
                    New
                  </span>
                )
              }
              onClick={handleClickProductUpdateMenu}
            />

            <Link to={'/settings'}>
              {({ isActive }) => <MenuItem icon={<Settings size={16} />} text="Settings" isActive={isActive} />}
            </Link>

            <MenuItem icon={<LogOut size={16} />} text="Log out" asChild={false} onClick={handleLogoutButton} />
          </div>

          {!dataUser?.hasSubscription && (
            <div className="px-3">
              <ViewPlan />
            </div>
          )}
        </SidebarContent>
      </Sidebar>
    </SidebarProvider>
  )
}
